// WP-5.2 (M17 MI-1..5, S-25 `/intel`): dimension breakdown queries + MI-5 saved views. The
// TrendChart reuses the existing `useReport('cashflow', ...)` hook directly (lib/reports.ts)
// — no separate trend query here.
import type { CurrencyCode, MiDimension, MiDirection } from '@pfos/engine';
import {
  miBreakdownResponseSchema,
  savedViewListResponseSchema,
  savedViewSchema,
  type MiBreakdownResponse,
  type SavedView,
  type SavedViewCreateRequest,
} from '@pfos/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from './api';

export const INTEL_KEY = ['intel'] as const;

export interface MiBreakdownFilters {
  direction: MiDirection;
  dimension: MiDimension;
  currency: CurrencyCode;
  from: string;
  to: string;
  tag?: string; // WP-C1.8 (SR-2)
}

export function useMiBreakdown(filters: MiBreakdownFilters): UseQueryResult<MiBreakdownResponse> {
  const params = new URLSearchParams({
    direction: filters.direction,
    dimension: filters.dimension,
    currency: filters.currency,
    from: filters.from,
    to: filters.to,
    ...(filters.tag !== undefined && filters.tag !== '' ? { tag: filters.tag } : {}),
  });
  return useQuery({
    queryKey: [...INTEL_KEY, 'breakdown', filters],
    queryFn: async () =>
      miBreakdownResponseSchema.parse(
        await api.get(`/api/v1/intel/breakdown?${params.toString()}`),
      ),
  });
}

const SAVED_VIEWS_KEY = [...INTEL_KEY, 'saved-views'] as const;

export function useSavedViews(): UseQueryResult<SavedView[]> {
  return useQuery({
    queryKey: SAVED_VIEWS_KEY,
    queryFn: async () =>
      savedViewListResponseSchema.parse(await api.get('/api/v1/intel/saved-views')).views,
  });
}

export function useCreateSavedView(): UseMutationResult<SavedView, Error, SavedViewCreateRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input) =>
      savedViewSchema.parse(await api.post('/api/v1/intel/saved-views', input)),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: SAVED_VIEWS_KEY });
    },
  });
}

export function useDeleteSavedView(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      await api.delete(`/api/v1/intel/saved-views/${id}`);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: SAVED_VIEWS_KEY });
    },
  });
}
